import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Edit3, Archive, Trash2, MessageCircle, Heart, Share2, Bookmark, Eye, Loader2 } from 'lucide-react';
import ReseauLayout from '../../components/reseau/ReseauLayout';
import useCockpitAction from '../../lib/useCockpitAction';
import { toast } from 'sonner';

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_LABEL = {
    draft: { label: "Brouillon", color: "bg-amber-100 text-amber-700" },
    scheduled: { label: "Programmée", color: "bg-blue-100 text-blue-700" },
    published: { label: "Publiée", color: "bg-emerald-100 text-emerald-700" },
    edited: { label: "Modifiée", color: "bg-emerald-100 text-emerald-700" },
    hidden: { label: "Masquée", color: "bg-slate-200 text-slate-700" },
    under_review: { label: "Modération", color: "bg-orange-100 text-orange-700" },
    rejected: { label: "Rejetée", color: "bg-red-100 text-red-700" },
    archived: { label: "Archivée", color: "bg-slate-200 text-slate-700" },
};

export default function ReseauMyPostsPage() {
    const [me, setMe] = useState(null);
    const [stats, setStats] = useState(null);
    const [tab, setTab] = useState("active");
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const runAction = useCockpitAction();

    const snapshotPosts = () => {
        let snap = null;
        setPosts(prev => { snap = prev; return prev; });
        return () => { if (snap) setPosts(snap); };
    };

    useEffect(() => {
        api.get("/auth/me").then(r => setMe(r.data));
    }, []);

    useEffect(() => {
        if (!me) return;
        loadStats(); loadTab(tab);
        // eslint-disable-next-line
    }, [me, tab]);

    const loadStats = async () => {
        try {
            const r = await api.get("/posts/cockpit/stats?days=30");
            setStats(r.data);
        } catch { /* ignore */ }
    };

    const loadTab = async (t) => {
        if (!me) return;
        setLoading(true);
        let statusFilter = null;
        if (t === "drafts") statusFilter = "draft";
        else if (t === "archived") statusFilter = "archived";
        else if (t === "moderation") statusFilter = "under_review";
        try {
            const url = statusFilter
                ? `/posts/by-author/${me.id}?status=${statusFilter}&limit=50`
                : `/posts/by-author/${me.id}?limit=50`;
            const r = await api.get(url);
            const filtered = (r.data.items || []).filter(p => {
                if (t === "active") return ["published", "edited"].includes(p.status);
                if (t === "drafts") return p.status === "draft";
                if (t === "archived") return p.status === "archived";
                if (t === "moderation") return ["under_review", "rejected"].includes(p.status);
                return true;
            });
            setPosts(filtered);
        } catch { setPosts([]); }
        finally { setLoading(false); }
    };

    const archive = async (id) => {
        if (!window.confirm("Archiver cette publication ?")) return;
        let revert = null;
        await runAction({
            key: `post-archive-${id}`,
            optimistic: () => {
                revert = snapshotPosts();
                setPosts(prev => prev.map(p => p.id === id ? { ...p, status: 'archived' } : p));
            },
            rollback:   () => { revert && revert(); },
            call:       () => api.post(`/posts/${id}/archive`),
            onSuccess:  () => { toast.success('Publication archivée. Elle n\'apparaît plus sur votre profil public ni dans le fil.'); loadTab(tab); },
            errorLabel: "L'archivage n'a pas été enregistré.",
        });
    };
    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement ?")) return;
        let revert = null;
        await runAction({
            key: `post-delete-${id}`,
            optimistic: () => {
                revert = snapshotPosts();
                setPosts(prev => prev.filter(p => p.id !== id));
            },
            rollback:   () => { revert && revert(); },
            call:       () => api.delete(`/posts/${id}`),
            onSuccess:  () => { toast.success('Publication supprimée. Action irréversible.'); loadTab(tab); loadStats(); },
            errorLabel: "La suppression n'a pas été enregistrée.",
        });
    };
    const publish = async (id) => {
        let revert = null;
        await runAction({
            key: `post-publish-${id}`,
            optimistic: () => {
                revert = snapshotPosts();
                setPosts(prev => prev.map(p => p.id === id ? { ...p, status: 'published' } : p));
            },
            rollback:   () => { revert && revert(); },
            call:       () => api.post(`/posts/${id}/publish`),
            onSuccess:  () => { toast.success('Publication mise en ligne. Elle apparaît dans votre fil et sur votre profil public.'); loadTab(tab); loadStats(); },
            errorLabel: "La publication n'a pas pu être enregistrée.",
        });
    };

    if (!me) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#681793]" />
            </div>
        );
    }

    return (
        <ReseauLayout headerTitle="Mes publications" headerSubtitle="Vos contenus territorialisés, brouillons, et statistiques d'impact 30 jours.">
            <main className="mx-auto px-4 sm:px-6 py-6 space-y-4" style={{ maxWidth: 'var(--reseau-maxw-page)' }}>
                {/* Stats résumées */}
                {stats && (
                    <section data-testid="my-posts-stats"
                             className="bg-white rounded-2xl border border-slate-200 p-5">
                        <h2 className="font-semibold mb-3">Activité 30 jours</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <Stat label="Publications" value={stats.totals?.publications} />
                            <Stat label="Réactions" value={stats.totals?.reactions} />
                            <Stat label="Commentaires" value={stats.totals?.comments} />
                            <Stat label="Partages" value={stats.totals?.shares} />
                            <Stat label="Sauvegardes" value={stats.totals?.saves} />
                            <Stat label="Vues totales" value={stats.totals?.views} />
                            <Stat label="Vues uniques" value={stats.totals?.unique_views} />
                            <Stat label="Territoires touchés" value={stats.territories?.length || 0} />
                        </div>
                        <div className="mt-3 text-xs text-slate-500">
                            Sur 7 jours : {stats.totals_7d?.publications ?? 0} publication(s),
                            {" "}{stats.totals_7d?.reactions ?? 0} réaction(s).
                        </div>
                    </section>
                )}

                {/* Onglets */}
                <div className="flex gap-1 border-b" data-testid="my-posts-tabs">
                    {[
                        { code: "active", label: "Publications actives" },
                        { code: "drafts", label: "Brouillons" },
                        { code: "moderation", label: "Modération" },
                        { code: "archived", label: "Archivées" },
                    ].map(t => (
                        <button key={t.code}
                                data-testid={`tab-${t.code}`}
                                onClick={() => setTab(t.code)}
                                className={`px-4 py-2 text-sm border-b-2 transition ${
                                    tab === t.code
                                        ? "border-[#681793] text-[#681793] font-semibold"
                                        : "border-transparent text-slate-600 hover:text-slate-900"
                                }`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Liste */}
                <section>
                    {loading && (
                        <div className="text-center py-8" data-testid="my-posts-loading">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#681793]" />
                        </div>
                    )}
                    {!loading && posts.length === 0 && (
                        <div className="bg-white rounded-2xl border p-8 text-center"
                             data-testid="my-posts-empty">
                            {tab === "active" && (
                                <>
                                    <h3 className="text-base font-semibold text-slate-800 mb-1">
                                        Une publication par mois suffit à entretenir votre visibilité locale
                                    </h3>
                                    <p className="text-sm text-slate-500 mb-4">
                                        Partagez un conseil, une analyse de quartier ou un retour d&apos;expérience.
                                        Vos publications nourrissent votre profil public et votre fil local.
                                    </p>
                                    <Link to="/reseau/fil?compose=1">
                                        <Button data-testid="cta-publish-from-empty"
                                                className="bg-[#681793] hover:bg-[#5a1380]">
                                            Composer ma première publication
                                        </Button>
                                    </Link>
                                </>
                            )}
                            {tab === "drafts" && (
                                <p className="text-sm text-slate-500">
                                    Aucun brouillon en attente. Vos publications enregistrées sans être publiées apparaîtront ici.
                                </p>
                            )}
                            {tab === "moderation" && (
                                <p className="text-sm text-slate-500">
                                    Aucune publication en modération.
                                </p>
                            )}
                            {tab === "archived" && (
                                <p className="text-sm text-slate-500">
                                    Aucune publication archivée. Les publications archivées ne sont plus visibles sur votre profil public.
                                </p>
                            )}
                        </div>
                    )}
                    {!loading && posts.map(p => (
                        <article key={p.id} data-testid={`my-post-${p.id}`}
                                 className="bg-white rounded-2xl border border-slate-200 p-4 mb-3">
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_LABEL[p.status]?.color || "bg-slate-200"}`}>
                                            {STATUS_LABEL[p.status]?.label || p.status}
                                        </span>
                                        {p.category_label && (
                                            <span className="text-xs text-slate-500">{p.category_label}</span>
                                        )}
                                        <span className="text-xs text-slate-400">{formatDate(p.published_at || p.edited_at)}</span>
                                    </div>
                                    {p.title && <h3 className="font-medium text-slate-900">{p.title}</h3>}
                                    <p className="text-sm text-slate-600 line-clamp-2">{p.body}</p>
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                    {p.status === "draft" && (
                                        <Button size="sm" data-testid={`publish-${p.id}-btn`}
                                                onClick={() => publish(p.id)}
                                                className="bg-[#681793] hover:bg-[#5a1380]">
                                            Publier
                                        </Button>
                                    )}
                                    {["published", "edited"].includes(p.status) && (
                                        <>
                                            <Button size="sm" variant="ghost" data-testid={`archive-${p.id}-btn`}
                                                    onClick={() => archive(p.id)} aria-label="Archiver">
                                                <Archive className="w-4 h-4" />
                                            </Button>
                                        </>
                                    )}
                                    <Button size="sm" variant="ghost" data-testid={`delete-${p.id}-btn`}
                                            onClick={() => remove(p.id)} aria-label="Supprimer"
                                            className="text-red-600">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                            {p.counters && (
                                <div className="mt-2 pt-2 border-t flex items-center gap-4 text-xs text-slate-500">
                                    <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {p.counters.reactions ?? 0}</span>
                                    <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {p.counters.comments ?? 0}</span>
                                    <span className="flex items-center gap-1"><Share2 className="w-3 h-3" /> {p.counters.shares ?? 0}</span>
                                    <span className="flex items-center gap-1"><Bookmark className="w-3 h-3" /> {p.counters.saves ?? 0}</span>
                                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {p.counters.views ?? 0}</span>
                                </div>
                            )}
                        </article>
                    ))}
                </section>
            </main>
        </ReseauLayout>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-slate-50 rounded-lg p-3" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-xs text-slate-500">{label}</div>
            <div className="text-2xl font-semibold text-slate-900">{value ?? 0}</div>
        </div>
    );
}
